<template>
    <div :id="id"></div>
</template>

<script>
export default {
    name: "google-ad-blog-display",
    props: {
        id: {type: String, required: true},
        adUnitPath: {type: String, required: true},

        sizeMapping: {
            type: Object,
            default: null
            //{ large: [...], small: [...], mobile: [...] }
        },
        bpLarge: {type: Array, default: () => [1588, 0]},
        bpSmall: {type: Array, default: () => [768, 0]},
        bpMobile: {type: Array, default: () => [0, 0]},

        topicId: {type: String, default: null},
        industryId: {type: String, default: null},

        refreshAdSlot: {type: String, default: "false"},
        slotRenderedEvent: {type: Function},

        viewThreshold: {type: Number, default: 0.5},
        rootMargin: {type: String, default: "0px"},
    },
    data() {
        return {
            slot: null,
            refreshTimer: null,
            onSlotRenderEnded: null,
            visibilityHandler: null,
            io: null,
            isInView: false,
            trackingEl: null,
            onMouseEnter: null,
            onMouseLeave: null,
        };
    },
    mounted() {
        if (!window.googletag || !googletag.cmd) return;

        googletag.cmd.push(() => {
            try {
                const existing = this.findSlotByElementId(this.id);
                let slot = existing;

                if (!slot) {
                    const isTakeover = this.adUnitPath.includes("takeover");
                    const mapping = this.buildSizeMapping(this.sizeMapping, isTakeover);

                    const allSizes = this.flattenSizes(this.sizeMapping);

                    slot = googletag
                        .defineSlot(this.adUnitPath, allSizes, this.id)
                        .defineSizeMapping(mapping)
                        .addService(googletag.pubads());
                }

                this.slot = slot;

                const targeting = {};

                if (this.industryId != null && this.industryId !== "122") {
                    targeting["Industry"] = this.industryId;
                } else if (this.adUnitPath.includes("Tungt_NO")) {
                    targeting["Industry"] = ["123", "124", "125", "128"];
                }

                if (this.topicId != null) {
                    targeting["Topic"] = this.topicId;
                }

                slot.setConfig({
                    targeting: targeting
                });

                this.onSlotRenderEnded = (event) => {
                    try {
                        const slotId =
                            event.slot &&
                            event.slot.getSlotElementId &&
                            event.slot.getSlotElementId();

                        if (slotId !== this.id) return;

                        this.registerAdSlot(slotId);

                        if (this.slotRenderedEvent) this.slotRenderedEvent(event);
                    } catch (e) {
                        console.warn("google-ad-display failed", this.id, e);
                    }
                };
                googletag.pubads().addEventListener("slotRenderEnded", this.onSlotRenderEnded);

                googletag.display(this.id);

                if (this.refreshAdSlot === "true") {
                    this.setupVisibilityPause();
                    this.setupViewabilityObserver();
                    this.evaluateRefreshState();
                }
            } catch (e) {
                console.warn("google-ad-display failed", this.id, e);
            }
        });
    },

    beforeDestroy() {
        this.stopRefreshTimer();

        if (this.visibilityHandler) {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
            this.visibilityHandler = null;
        }

        if (this.io) {
            this.io.disconnect();
            this.io = null;
        }

        this.unregisterAdSlot();

        if (window.googletag && googletag.cmd && this.onSlotRenderEnded) {
            const handler = this.onSlotRenderEnded;
            googletag.cmd.push(() => {
                try {
                    googletag.pubads().removeEventListener("slotRenderEnded", handler);
                } catch (e) {
                    //Empty catch
                }
            });
        }
    },

    methods: {
        setupViewabilityObserver() {
            const el = this.$el;
            if (!el) return;

            if (!("IntersectionObserver" in window)) {
                this.isInView = true;
                this.evaluateRefreshState();
                return;
            }

            this.io = new IntersectionObserver(
                (entries) => {
                    const entry = entries && entries[0];
                    this.isInView = !!(entry && entry.isIntersecting && entry.intersectionRatio >= this.viewThreshold);
                    this.evaluateRefreshState();
                },
                {
                    root: null,
                    rootMargin: this.rootMargin,
                    threshold: [this.viewThreshold]
                }
            );

            this.io.observe(el);
        },

        setupVisibilityPause() {
            this.visibilityHandler = () => {
                this.evaluateRefreshState();
            };
            document.addEventListener("visibilitychange", this.visibilityHandler);
        },

        registerAdSlot(slotId) {
            const slotEl = document.getElementById(slotId);
            if (!slotEl) return;

            if (this.trackingEl === slotEl) return;

            this.unregisterAdSlot();

            const wrapper = slotEl;
            wrapper.dataset.adSlotId = slotId;
            if (!wrapper.style.position) wrapper.style.position = "relative";

            this.onMouseEnter = () => {
                window.adTracker = window.adTracker || {hoverState: {}, slots: {}};
                window.adTracker.hoverState.currentSlotId = slotId;
                window.adTracker.hoverState.lastChangeTs = Date.now();
            };

            this.onMouseLeave = () => {
                if (window.adTracker?.hoverState?.currentSlotId === slotId) {
                    window.adTracker.hoverState.currentSlotId = null;
                    window.adTracker.hoverState.lastChangeTs = Date.now();
                }
            };

            wrapper.addEventListener("mouseenter", this.onMouseEnter);
            wrapper.addEventListener("mouseleave", this.onMouseLeave);

            window.adTracker = window.adTracker || {hoverState: {}, slots: {}};
            window.adTracker.slots[slotId] = wrapper;

            this.trackingEl = wrapper;
        },

        unregisterAdSlot() {
            if (this.trackingEl && this.onMouseEnter && this.onMouseLeave) {
                this.trackingEl.removeEventListener("mouseenter", this.onMouseEnter);
                this.trackingEl.removeEventListener("mouseleave", this.onMouseLeave);
            }
            this.trackingEl = null;
            this.onMouseEnter = null;
            this.onMouseLeave = null;
        },

        evaluateRefreshState() {
            const pageVisible = !document.hidden;
            const shouldRun = pageVisible && this.isInView && this.refreshAdSlot === "true";
            if (shouldRun) this.startRefreshTimer();
            else this.stopRefreshTimer();
        },

        startRefreshTimer() {
            if (this.refreshTimer) return;
            if (!this.slot) return;
            if (this.refreshAdSlot !== "true") return;

            const jitter = Math.floor(Math.random() * 2000);

            setTimeout(() => {
                if (this.refreshTimer) return;
                if (document.hidden || !this.isInView || this.refreshAdSlot !== "true") return;

                this.refreshTimer = setInterval(() => {
                    if (document.hidden || !this.isInView) return;
                    if (!window.googletag || !googletag.cmd) return;

                    googletag.cmd.push(() => {
                        try {
                            if (this.slot && typeof googletag.pubads().refresh === "function") {
                                googletag.pubads().refresh([this.slot]);
                            }
                        } catch (e) {
                            console.warn("Failed to refresh ad slot", this.id, e.message || e, e.stack || "")
                        }
                    });
                }, 15000);
            }, jitter);
        },

        stopRefreshTimer() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
        },

        findSlotByElementId(id) {
            try {
                const pubads = googletag.pubads && googletag.pubads();
                const slots = pubads && pubads.getSlots ? pubads.getSlots() : [];
                return slots.find((s) => s.getSlotElementId && s.getSlotElementId() === id) || null;
            } catch (e) {
                return null;
            }
        },
        buildSizeMapping(map, isTakeover) {
            const sm = googletag.sizeMapping();

            if (map.large?.length) sm.addSize(this.bpLarge, map.large);
            if (map.small?.length) sm.addSize(this.bpSmall, map.small);

            const currentDate = new Date();
            if (isTakeover) {
                if (currentDate < new Date("2027-01-01")) {
                    sm.addSize(this.bpMobile, [300, 100]);
                } else {
                    sm.addSize(this.bpMobile, [360, 125]);
                }
            } else {
                if (map.mobile?.length) sm.addSize(this.bpMobile, map.mobile);
            }
            
            return sm.build();
        },

        flattenSizes(map) {
            const all = []
                .concat(map.large || [], map.small || [], map.mobile || []);

            const seen = new Set();
            const out = [];
            for (const s of all) {
                const key = Array.isArray(s) ? `${s[0]}x${s[1]}` : String(s);
                if (!seen.has(key)) {
                    seen.add(key);
                    out.push(s);
                }
            }
            return out;
        },
    }
};
</script>